<template>
  <transition name="modal-fade">
    <div v-if="visible" class="fixed inset-0 z-[1050] flex items-start justify-center overflow-y-auto py-7" role="dialog" aria-modal="true" :aria-labelledby="title ? 'base-modal-title' : undefined" @mousedown="onOverlayMousedown">
      <div class="modal-backdrop fixed inset-0 bg-black/20 backdrop-blur-[4px] -z-1"></div>
      <div class="modal-dialog relative w-full max-w-[500px] md:max-w-[700px] mx-auto pointer-events-none" :class="dialogClass">
        <div class="relative bg-white rounded-[24px] pointer-events-auto w-full shadow-[0_16px_48px_rgba(0,0,0,0.1),0_4px_16px_rgba(0,0,0,0.06)]" :class="contentClass" ref="content">
          <!-- Header -->
          <div v-if="!hideHeader" class="flex items-center justify-between px-[25px] pt-[25px]" :class="headerClass">
            <h5 id="base-modal-title" class="text-lg font-semibold text-ink m-0">{{ title }}</h5>
            <button type="button" data-modal-close class="flex items-center justify-center w-8 h-8 rounded-xl text-muted bg-transparent border-none cursor-pointer p-0 ml-auto transition-[background,color] duration-150 hover:bg-[#f0f0f5] hover:text-ink" aria-label="Close" @click="hide">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>

          <!-- Body -->
          <div class="p-[25px]" :class="bodyClass">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div v-if="!hideFooter" class="flex justify-end gap-2.5 px-[25px] pb-[25px]" :class="footerClass">
            <slot name="modal-footer" :cancel="hide">
              <button type="button"
                class="border-none rounded-full py-2.5 px-6 text-sm font-medium cursor-pointer transition-all duration-200 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed bg-[#f4f5fa] text-ink"
                :disabled="loading" @click="onCancel">{{ cancelTitle }}</button>
              <button type="button"
                class="border-none rounded-full py-2.5 px-6 text-sm font-medium cursor-pointer transition-all duration-200 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed"
                :class="okBtnClass"
                :disabled="loading" @click="onOk"><svg v-if="loading" class="animate-spin -ml-0.5 mr-1.5 inline-block" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>{{ loading ? 'Saving...' : okTitle }}</button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'BaseModal',
  props: {
    title: { type: String, default: '' },
    okTitle: { type: String, default: 'OK' },
    cancelTitle: { type: String, default: 'Cancel' },
    okVariant: { type: String, default: 'primary' },
    cancelVariant: { type: String, default: 'secondary' },
    hideFooter: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
    contentClass: { type: [String, Array, Object], default: '' },
    headerClass: { type: [String, Array, Object], default: '' },
    bodyClass: { type: [String, Array, Object], default: '' },
    footerClass: { type: [String, Array, Object], default: '' },
    size: { type: String, default: '' },
    centered: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    noClose: { type: Boolean, default: false }
  },
  data () {
    return {
      visible: false,
      previousFocus: null
    }
  },
  computed: {
    dialogClass () {
      const classes = []
      if (this.size === 'xl') classes.push('!max-w-[1140px]')
      else if (this.size === 'lg') classes.push('!max-w-[800px]')
      else if (this.size === 'sm') classes.push('!max-w-[300px]')
      if (this.centered) classes.push('flex items-center min-h-[calc(100vh-3.5rem)]')
      return classes
    },
    okBtnClass () {
      switch (this.okVariant) {
        case 'danger': return 'bg-[#e53935] text-white'
        case 'success': return 'bg-[#1AFFBA] text-ink'
        default: return 'bg-ink text-white'
      }
    }
  },
  methods: {
    show () {
      this.previousFocus = document.activeElement
      this.$emit('show')
      this.visible = true
      document.body.classList.add('modal-open')
      this.$nextTick(() => {
        document.addEventListener('keydown', this.onEscape)
        if (this.$refs.content) {
          const focusable = this.$refs.content.querySelector('input, textarea, select, button:not([data-modal-close])')
          if (focusable) focusable.focus()
        }
      })
    },
    hide () {
      this.visible = false
      document.body.classList.remove('modal-open')
      document.removeEventListener('keydown', this.onEscape)
      this.$emit('hidden')
      if (this.previousFocus) {
        this.$nextTick(() => {
          this.previousFocus.focus()
          this.previousFocus = null
        })
      }
    },
    onOk () {
      let prevented = false
      const evt = {
        preventDefault () { prevented = true }
      }
      this.$emit('ok', evt)
      if (!prevented) {
        this.hide()
      }
    },
    onCancel () {
      this.$emit('cancel')
      this.hide()
    },
    onOverlayMousedown (e) {
      if (this.noClose) return
      if (e.target === this.$el || e.target.classList.contains('modal-backdrop')) {
        this.hide()
      }
    },
    onEscape (e) {
      if (this.noClose) return
      if (e.key === 'Escape') {
        this.hide()
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onEscape)
    if (this.visible) {
      document.body.classList.remove('modal-open')
    }
  }
}
</script>

<style>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .modal-dialog,
.modal-fade-leave-active .modal-dialog {
  transition: transform 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .modal-dialog {
  transform: translateY(12px);
}

.modal-fade-leave-to .modal-dialog {
  transform: translateY(-12px);
}
</style>
